import { Badge } from "@ui/Atoms/Badge/Badge";
import { IconSearch } from "@ui/Icons/IconSearch";
import { PlaceMap } from "@ui/Molecules/Map/PlaceMap";
import { PlaceListRow } from "@ui/Molecules/Place/ListRow/PlaceListRow";
import { FilterRail } from "@ui/Organisms/Filter/FilterRail";
import { AppShell } from "@ui/Organisms/Layout/AppShell";
import { MapChrome } from "@ui/Organisms/MapChrome/MapChrome";
import type { MapResult, MapTemplateProps } from "./types";

function resultBadges(result: MapResult, openLabel: string) {
	if (!result.open && !result.price) return undefined;
	return (
		<>
			{result.open && (
				<Badge tone="open" size="sm">
					{openLabel}
				</Badge>
			)}
			{result.price && (
				<Badge tone="outline" size="sm">
					{result.price}
				</Badge>
			)}
		</>
	);
}

export function MapTemplate({
	topBar,
	tabBar,
	translations: t,
	mapPlaces,
	activePlaceId,
	curatorLogo,
	onPinClick,
	loadPreview,
	filterSections,
	filterHandlers,
	onClearFilters,
	quickActions,
	activeChips,
	lowResults,
	onLocate,
	results,
	onOpenSearch,
}: MapTemplateProps) {
	return (
		<AppShell topBar={topBar} tabBar={tabBar} fullBleed>
			<div className="relative h-full">
				{/* Single map layer: fills the middle column on desktop, full-bleed on mobile */}
				<div className="absolute inset-y-0 left-0 right-0 bg-[var(--map-land)] md:left-[296px] md:right-[380px]">
					<PlaceMap
						places={mapPlaces}
						activeId={activePlaceId}
						curatorLogo={curatorLogo ?? null}
						onPinClick={onPinClick}
						loadPreview={loadPreview}
						fit="places"
					/>
					<MapChrome
						quickActions={quickActions}
						activeChips={activeChips}
						onClearAll={onClearFilters}
						clearAllLabel={t.clearAll}
						lowResults={lowResults}
						onLocate={onLocate}
						locateLabel={t.locateLabel}
					/>
				</div>

				{/* Desktop filter rail */}
				<div className="absolute inset-y-0 left-0 hidden w-[296px] overflow-y-auto border-r border-[var(--border-default)] bg-[var(--surface-default)] md:block">
					<FilterRail
						sections={filterSections}
						handlers={filterHandlers}
						translations={{ title: t.title, clear: t.clear }}
						onClear={onClearFilters}
					/>
				</div>

				{/* Desktop results list */}
				<div className="absolute inset-y-0 right-0 hidden w-[380px] flex-col overflow-y-auto border-l border-[var(--border-default)] bg-[var(--surface-default)] md:flex">
					<div className="sticky top-0 z-[1] flex items-baseline justify-between border-b border-[var(--border-subtle)] bg-[var(--surface-default)] px-6 py-4">
						<div
							className="text-[19px] font-extrabold tracking-[-0.025em]"
							style={{ fontFamily: "var(--font-display)" }}
						>
							{t.listTitle}
						</div>
						<div className="text-[13px] text-[var(--content-muted)]">
							{t.resultCount}
						</div>
					</div>
					<div className="px-6 pb-10">
						{results.map((result) => (
							<PlaceListRow
								key={result.id}
								id={result.id}
								name={result.name}
								href={result.href}
								area={result.area}
								distance={result.distance}
								meta={result.meta}
								cover={result.cover}
								videoUrl={result.videoUrl}
								emptyLabel={t.emptyLabel}
								badges={resultBadges(result, t.openLabel)}
							/>
						))}
					</div>
				</div>

				{/* Mobile search entry */}
				<button
					type="button"
					onClick={onOpenSearch}
					className="absolute inset-x-5 top-[calc(14px+env(safe-area-inset-top))] z-[3] flex items-center gap-2.5 rounded-[var(--radius-sm)] bg-[var(--surface-default)] px-3.5 py-3 text-left shadow-[var(--shadow-dropdown)] md:hidden"
				>
					<IconSearch size={18} color="var(--content-muted)" />
					<span className="text-[14px] text-[var(--content-muted)]">
						{t.searchPlaceholder}
					</span>
				</button>

				{/* Mobile bottom sheet */}
				<div className="absolute inset-x-0 bottom-[calc(84px+env(safe-area-inset-bottom))] z-[3] flex max-h-[42vh] flex-col rounded-t-[var(--radius-lg)] bg-[var(--surface-default)] pt-2.5 shadow-[0_-6px_20px_rgb(17_17_17_/_0.12)] md:hidden">
					<span className="mx-auto mb-2.5 block h-1 w-9 shrink-0 rounded-[var(--radius-pill)] bg-[var(--border-strong)]" />
					<div className="flex shrink-0 items-baseline justify-between px-5 pb-1.5">
						<div
							className="text-[16px] font-extrabold tracking-[-0.025em]"
							style={{ fontFamily: "var(--font-display)" }}
						>
							{t.nearbyTitle}
						</div>
						<div className="text-[12.5px] text-[var(--content-muted)]">
							{t.resultCount}
						</div>
					</div>
					<div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
						{results.map((result) => (
							<PlaceListRow
								key={result.id}
								size="sm"
								id={result.id}
								name={result.name}
								href={result.href}
								area={result.area?.split(",")[0]}
								distance={result.distance}
								cover={result.cover}
								videoUrl={result.videoUrl}
								emptyLabel={t.emptyLabel}
							/>
						))}
					</div>
				</div>
			</div>
		</AppShell>
	);
}
